// ==========================================
// src/pages/BuscarCargas.jsx — SEM DATAS
// ✅ CARGAS REAIS primeiro (carga_ficticia = false)
// ✅ Cargas fictícias depois (carga_ficticia = true)
// ✅ Badge real (Disponível piscando) vs fictícia (Disponível para Análise)
// ✅ Card Borinho com imagem borinhobuscarcargas02 (sem frases)
// ✅ CORREÇÃO: Removido filtro aprovada_admin — carga não precisa de aprovação!
// ✅ CORREÇÃO: Valores em tonelada mostram R$ XX,XX / ton (não valor total)
// ✅ REMOVIDO: datas de coleta e entrega dos cards (data_coleta, hora_coleta, data_entrega, hora_entrega)
// ==========================================

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { supabase } from '@/lib/supabase';
import {
  Package, Loader2, Search, MapPin, Truck, ArrowRight, Filter, X,
  ChevronDown, Settings
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

// ==================== DADOS COMPLETOS ====================
const estadosBrasil = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

const tiposVeiculo = [
  'Carreta Simples',
  'Carreta LS',
  'Vanderléia',
  'Rodotrem',
  'Bitrem',
  'Truck',
  'Toco',
  'VUC',
  'Furgão'
];

const tiposCarga = [
  'Granel',
  'Frigorificada',
  'Seca',
  'Perigosa',
  'Líquida',
  'Container',
  'Sacos',
  'Paletizado',
  'Big Bag',
  'Sucata',
  'Fardos'
];

// ==================== FORMATADOR DE VALOR — CORREÇÃO: TONELADA ====================
const formatarValorCarga = (carga) => {
  const unidade = (carga.peso_unidade || "").toLowerCase().trim();
  const peso = parseFloat(carga.peso_valor || carga.peso || 0);
  const valorTotal = parseFloat(carga.valor_total || carga.valor_frete || carga.valor || 0);

  if (unidade === "ton" || unidade === "tonelada" || unidade === "toneladas") {
    if (valorTotal > 0 && peso > 0) {
      const valorPorTon = valorTotal / peso;
      return `R$ ${valorPorTon.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / ton`;
    }
    return "A combinar";
  }

  if (!valorTotal) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(valorTotal);
};

// ==================== SIDEBAR COM BUSCA AUTOMÁTICA ====================
function SidebarFiltros({ filtros, setFiltros, onBuscar, onLimpar }) {
  const carrocerias = [
    { id: 'bau', label: 'Baú' },
    { id: 'cacamba', label: 'Caçamba' },
    { id: 'graneleiro', label: 'Graneleiro' },
    { id: 'frigorifico', label: 'Frigorífico' },
  ];

  const atualizarFiltro = (campo, valor) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
    setTimeout(() => onBuscar(), 50);
  };

  return (
    <div className="w-full lg:w-[280px] flex-shrink-0 space-y-4">
      {/* CARD BORINHO BUSCAR CARGAS */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="flex flex-col items-center text-center">
          <div className="w-full flex justify-center">
            <img
              src="/borinhobuscarcargas02.png"
              alt="Borinho - Mascote BoraFrete"
              className="w-full h-auto object-contain rounded-lg"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="w-32 h-32 bg-blue-100 rounded-full flex items-center justify-center hidden">
              <span className="text-5xl">👍</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-4">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Filter className="w-4 h-4 text-blue-600" />
          Filtros
        </h3>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">Origem</label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Cidade, estado ou região"
              value={filtros.origem || ''}
              onChange={(e) => atualizarFiltro('origem', e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">Destino</label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Cidade, estado ou região"
              value={filtros.destino || ''}
              onChange={(e) => atualizarFiltro('destino', e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">Estado Origem</label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={filtros.origem_estado || ''}
              onChange={(e) => atualizarFiltro('origem_estado', e.target.value)}
              className="w-full pl-9 pr-8 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all appearance-none bg-white"
            >
              <option value="">Todos</option>
              {estadosBrasil.map(uf => (
                <option key={uf} value={uf}>{uf}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">Estado Destino</label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={filtros.destino_estado || ''}
              onChange={(e) => atualizarFiltro('destino_estado', e.target.value)}
              className="w-full pl-9 pr-8 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all appearance-none bg-white"
            >
              <option value="">Todos</option>
              {estadosBrasil.map(uf => (
                <option key={uf} value={uf}>{uf}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">Tipo de caminhão</label>
          <div className="relative">
            <Truck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={filtros.tipo_veiculo || ''}
              onChange={(e) => atualizarFiltro('tipo_veiculo', e.target.value)}
              className="w-full pl-9 pr-8 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all appearance-none bg-white"
            >
              <option value="">Selecione</option>
              {tiposVeiculo.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">Tipo de carga</label>
          <div className="relative">
            <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={filtros.tipo_carga || ''}
              onChange={(e) => atualizarFiltro('tipo_carga', e.target.value)}
              className="w-full pl-9 pr-8 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all appearance-none bg-white"
            >
              <option value="">Selecione</option>
              {tiposCarga.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Buscar por carroceria
          </label>
          <div className="grid grid-cols-2 gap-2">
            {carrocerias.map((car) => (
              <button
                key={car.id}
                onClick={() => atualizarFiltro('carroceria', car.id === filtros.carroceria ? '' : car.id)}
                className={`flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg border text-xs font-medium transition-all ${
                  filtros.carroceria === car.id
                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                <span className="truncate">{car.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2 pt-2">
          <button
            onClick={onBuscar}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-3 rounded-lg font-semibold text-sm hover:bg-blue-700 active:scale-[0.98] transition-all shadow-sm"
          >
            <Search className="w-4 h-4" />
            Buscar cargas
          </button>

          {(filtros.busca || filtros.origem || filtros.destino || filtros.origem_estado || filtros.destino_estado || filtros.tipo_veiculo || filtros.tipo_carga || filtros.carroceria) && (
            <button
              onClick={onLimpar}
              className="w-full flex items-center justify-center gap-2 bg-white text-gray-600 border border-gray-300 py-2.5 rounded-lg font-medium text-sm hover:bg-gray-50 transition-all"
            >
              <X className="w-4 h-4" />
              Limpar filtros
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== BADGE DISPONÍVEL - PISCANDO (CARGAS REAIS) ====================
function BadgeDisponivelReal() {
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200 animate-pulse">
      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-1.5 animate-ping"></span>
      Disponível
    </span>
  );
}

// ==================== BADGE DISPONÍVEL PARA ANÁLISE - ESTÁTICO (CARGAS FICTÍCIAS) ====================
function BadgeDisponivelFicticia() {
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200">
      <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-1.5"></span>
      Disponível para Análise
    </span>
  );
}

// ==================== CARD DE CARGA - SEM DATAS ====================
function CardCargaLimpo({ carga, user, onVerDetalhes, onLoginClick }) {
  const isLogado = !!user;
  const isAprovado = user?.cadastro_aprovado === true;
  const isFicticia = carga.carga_ficticia === true;

  const getBadge = () => {
    if (carga.tipo_carga === 'Granel') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 border border-blue-200">
          Carga completa
        </span>
      );
    }
    if (carga.tipo_carga === 'Frigorificada' || carga.tipo_carga === 'Líquida') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700 border border-green-200">
          Carga fracionada
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-600 border border-gray-200">
        {carga.tipo_carga || 'Carga'}
      </span>
    );
  };

  const formatarPeso = (peso, pesoUnidade) => {
    if (!peso) return '-';

    if (pesoUnidade) {
      const unidade = pesoUnidade.toLowerCase();
      if (unidade === 'ton') return `${peso} ton`;
      if (unidade === 'm3' || unidade === 'metro_cubico') return `${peso} m³`;
      return `${peso} kg`;
    }

    const num = parseFloat(peso.toString().replace(/[^\d.]/g, ''));
    if (isNaN(num)) return peso;

    if (num >= 1000) {
      return `${num} kg`;
    }
    return `${num} ton`;
  };

  const handleClick = () => {
    if (!isLogado) {
      onLoginClick();
      return;
    }
    if (!isAprovado) {
      toast.info('Aguardando aprovação do cadastro');
      return;
    }
    onVerDetalhes(carga);
  };

  return (
    <div className={`bg-white rounded-xl border p-5 hover:shadow-lg hover:border-blue-200 transition-all duration-300 ${
      isFicticia ? 'border-gray-200 opacity-80' : 'border-gray-200'
    }`}>
      <div className="flex flex-col gap-4">
        {/* Linha superior: Badge tipo + Badge Disponível + Valor */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getBadge()}
            {isFicticia ? <BadgeDisponivelFicticia /> : <BadgeDisponivelReal />}
          </div>
          {isLogado && isAprovado && (
            <span className="text-sm font-bold text-blue-600">{formatarValorCarga(carga)}</span>
          )}
        </div>

        {/* Origem → Destino — SEM DATAS */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <p className="font-bold text-gray-900 text-sm">
              {carga.origem_cidade} - {carga.origem_estado}
            </p>
          </div>

          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center">
              <ArrowRight className="w-4 h-4 text-blue-500" />
            </div>
          </div>

          <div className="flex-1">
            <p className="font-bold text-gray-900 text-sm">
              {carga.destino_cidade} - {carga.destino_estado}
            </p>
          </div>
        </div>

        {/* Grid de informações */}
        <div className="grid grid-cols-3 gap-4 pt-3 border-t border-gray-100">
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">Tipo de carga</p>
            <p className="text-xs font-semibold text-gray-800 mt-0.5">{carga.produto || carga.tipo_carga || 'Não informado'}</p>
          </div>
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">Carroceria</p>
            <p className="text-xs font-semibold text-gray-800 mt-0.5">{carga.carroceria || carga.tipo_veiculo || 'Não informada'}</p>
          </div>
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">Peso</p>
            <p className="text-xs font-semibold text-gray-800 mt-0.5">{formatarPeso(carga.peso, carga.peso_unidade)}</p>
          </div>
        </div>

        {/* Botão Ver detalhes */}
        <div className="flex justify-end pt-2">
          <button
            onClick={handleClick}
            className="px-5 py-2 text-xs font-semibold text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-all active:scale-[0.98]"
          >
            {isLogado ? 'Ver detalhes' : 'Fazer login para ver detalhes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== PULL TO REFRESH ====================
function PullToRefresh({ children, onRefresh }) {
  return (
    <div className="relative">
      <div className="absolute top-0 left-0 right-0 z-10 flex justify-center pt-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          className="text-slate-500 hover:text-blue-600"
        >
          <Loader2 className="w-4 h-4 mr-1" />
          Atualizar lista
        </Button>
      </div>
      {children}
    </div>
  );
}

// ==================== COMPONENTE PRINCIPAL ====================
export default function BuscarCargas() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, isAdmin } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [cargas, setCargas] = useState([]);
  const [error, setError] = useState(null);
  const [ordenacao, setOrdenacao] = useState('recentes');

  const [filtros, setFiltros] = useState({
    busca: '',
    origem: '',
    destino: '',
    origem_estado: searchParams.get('origem') || '',
    destino_estado: searchParams.get('destino') || '',
    tipo_veiculo: '',
    tipo_carga: '',
    carroceria: ''
  });

  useEffect(() => {
    const buscarCargas = async () => {
      try {
        setIsLoading(true);

        const { data, error } = await supabase
          .from('cargas')
          .select('*')
          .eq('status', 'disponivel')
          .order('carga_ficticia', { ascending: true })
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Erro Supabase BuscarCargas:', error);
          throw error;
        }

        if (data && data.length > 0) {
          console.log('✅ Cargas carregadas:', data.length);
          const ficticias = data.filter(c => c.carga_ficticia === true).length;
          const reais = data.length - ficticias;
          console.log(`📊 Reais: ${reais} | Fictícias: ${ficticias}`);
          setCargas(data);
        } else {
          console.log('⚠️ Nenhuma carga no Supabase');
          setCargas([]);
        }
      } catch (err) {
        console.error('Erro ao buscar cargas:', err);
        setCargas([]);
      } finally {
        setIsLoading(false);
      }
    };

    buscarCargas();

    const handleCargaExcluida = () => buscarCargas();
    window.addEventListener('cargaExcluida', handleCargaExcluida);

    const handleStorageChange = (e) => {
      if (e.key === 'borafrete_cargas_excluidas') {
        buscarCargas();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('cargaExcluida', handleCargaExcluida);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const handleLogin = useCallback(() => {
    const currentPath = window.location.pathname + window.location.search;
    navigate('/login?from=' + encodeURIComponent(currentPath));
  }, [navigate]);

  const handleVerDetalhes = useCallback((carga) => {
    navigate(`/carga/${carga.id}`);
  }, [navigate]);

  const handleRefresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('cargas')
        .select('*')
        .eq('status', 'disponivel')
        .order('carga_ficticia', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data && data.length > 0) {
        setCargas(data);
      } else {
        setCargas([]);
      }
    } catch (err) {
      console.error('Erro no refresh:', err);
      setCargas([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const cargasFiltradas = useMemo(() => {
    if (!Array.isArray(cargas) || cargas.length === 0) return [];

    return cargas.filter(carga => {
      if (filtros.origem_estado && carga.origem_estado !== filtros.origem_estado) return false;
      if (filtros.destino_estado && carga.destino_estado !== filtros.destino_estado) return false;
      if (filtros.tipo_veiculo && carga.tipo_veiculo !== filtros.tipo_veiculo) return false;
      if (filtros.tipo_carga && carga.tipo_carga !== filtros.tipo_carga) return false;

      if (filtros.origem) {
        const busca = filtros.origem.toLowerCase().trim();
        const matchOrigem = carga.origem_cidade?.toLowerCase().includes(busca) ||
                           carga.origem_estado?.toLowerCase().includes(busca);
        if (!matchOrigem) return false;
      }
      if (filtros.destino) {
        const busca = filtros.destino.toLowerCase().trim();
        const matchDestino = carga.destino_cidade?.toLowerCase().includes(busca) ||
                            carga.destino_estado?.toLowerCase().includes(busca);
        if (!matchDestino) return false;
      }
      if (filtros.busca?.trim()) {
        const busca = filtros.busca.toLowerCase().trim();
        const match =
          carga.titulo?.toLowerCase().includes(busca) ||
          carga.origem_cidade?.toLowerCase().includes(busca) ||
          carga.destino_cidade?.toLowerCase().includes(busca) ||
          carga.produto?.toLowerCase().includes(busca);
        if (!match) return false;
      }

      return true;
    });
  }, [cargas, filtros]);

  const limparFiltros = useCallback(() => {
    setFiltros({
      busca: '',
      origem: '',
      destino: '',
      origem_estado: '',
      destino_estado: '',
      tipo_veiculo: '',
      tipo_carga: '',
      carroceria: ''
    });
    setTimeout(() => handleRefresh(), 50);
  }, [handleRefresh]);

  const loading = useMemo(() => isLoading || authLoading, [isLoading, authLoading]);

  const cargasOrdenadas = useMemo(() => {
    const sorted = [...cargasFiltradas];
    if (ordenacao === 'recentes') {
      return sorted;
    }
    if (ordenacao === 'maior_preco') {
      return sorted.sort((a, b) => parseFloat(b.valor || 0) - parseFloat(a.valor || 0));
    }
    return sorted;
  }, [cargasFiltradas, ordenacao]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6">
          <div className="text-center py-20 bg-white rounded-xl border border-gray-200">
            <h3 className="text-lg font-medium text-red-600 mb-2">Erro ao carregar cargas</h3>
            <p className="text-gray-600">{error.message || 'Tente novamente mais tarde.'}</p>
            <Button onClick={handleRefresh} className="mt-4 bg-blue-600 hover:bg-blue-700">Tentar novamente</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row gap-6">
            <SidebarFiltros
              filtros={filtros}
              setFiltros={setFiltros}
              onBuscar={handleRefresh}
              onLimpar={limparFiltros}
            />

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h1 className="text-lg font-bold text-gray-900">Fretes disponíveis</h1>
                  <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                    {cargasOrdenadas.length} {cargasOrdenadas.length === 1 ? 'carga encontrada' : 'cargas encontradas'}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  {isAdmin && (
                    <button
                      onClick={() => navigate('/admin/cargas-pendentes')}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-white text-xs font-medium rounded-lg hover:bg-slate-700 transition-colors"
                    >
                      <Settings className="w-3.5 h-3.5" />
                      Gerenciar
                    </button>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Ordenar por</span>
                    <div className="relative">
                      <select
                        value={ordenacao}
                        onChange={(e) => setOrdenacao(e.target.value)}
                        className="pl-3 pr-8 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none appearance-none bg-white cursor-pointer"
                      >
                        <option value="recentes">Mais recentes</option>
                        <option value="maior_preco">Maior preço</option>
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin mb-3" />
                  <p className="text-sm text-gray-500">Carregando cargas...</p>
                </div>
              ) : cargasOrdenadas.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhuma carga encontrada</h3>
                  <p className="text-sm text-gray-500 mb-4">Não há cargas disponíveis no momento. Volte mais tarde!</p>
                  <button
                    onClick={limparFiltros}
                    className="px-4 py-2 text-sm font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    Limpar filtros
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {cargasOrdenadas.map((carga) => (
                    <CardCargaLimpo
                      key={carga.id}
                      carga={carga}
                      user={user}
                      onVerDetalhes={handleVerDetalhes}
                      onLoginClick={handleLogin}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </PullToRefresh>
  );
}
